import * as React from "react";
import {
  ApiError,
  getCurrentTenant,
  type LoginResponse,
  type MeResponse,
  type SupportSessionExchangeResponse,
  type SupportSessionView,
  type TenantFeatureFlagsView,
  type UserPublic,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import {
  clearSupportSession,
  isSupportSessionRoute,
  readSupportSession,
  storeSupportSession,
} from "@/auth/supportSession";

/**
 * Public login input for the SPA — only what the user actually types.
 * The tenant slug travels via the `X-Tenant-Slug` header (set by the
 * shared API client from the URL subdomain) and never appears in this
 * shape, which is why we don't reuse the BE's `LoginRequest` type here.
 */
export interface PortalLoginInput {
  email: string;
  password: string;
  /** Optional override; when omitted the API client injects the slug from the URL. */
  tenant_id?: string;
}

/**
 * Auth state shape
 * ----------------
 * - `status` is the canonical FSM: `loading` (we're fetching /auth/me to
 *   see whether the HttpOnly refresh cookie is still valid), `authed`,
 *   `anonymous`.
 * - `user` is populated only in `authed`.
 * - The access token lives inside the API client (in-memory only) and is
 *   never stored here; this state is purely UI-facing.
 */

type AuthStatus = "loading" | "authed" | "anonymous";

interface AuthState {
  status: AuthStatus;
  user: UserPublic | null;
  /**
   * Phase C1a — IA tenant first-run wizard gate. Mirrors
   * `MeResponse.first_run_complete`. `null` means we haven't fetched
   * `/auth/me` yet (status `loading`) OR the user is a super-admin
   * (whose synthetic tenant is never gated). Treat `null` like `true`
   * for routing decisions.
   */
  firstRunComplete: boolean | null;
  /**
   * Phase H5d — per-tenant feature flags resolved from
   * `MeResponse.feature_flags`. `null` means we haven't fetched
   * `/auth/me` yet OR the user is a super_admin (synthetic platform
   * tenant — no tenant-scoped flags). Treat `null` as "no gating",
   * matching the BE's documented contract.
   *
   * Currently consumed by `AssessmentTemplatesPage` +
   * `AssessmentTemplateDetailPage` + `AssessmentTemplateCreateSheet`
   * to hide write controls + show an explanatory banner when
   * `assessments_authoring_by_tenant === false`. Forms-side gating
   * is enforced BE-side only (Phase B2 — no IA banner needed today).
   */
  featureFlags: TenantFeatureFlagsView | null;
  /**
   * Phase SC-22 — set while this tab is an EduViaOS super-admin support
   * session (owner access, every change audited with the SA's identity).
   */
  supportSession: SupportSessionView | null;
  /** Phase SC-22 — the support session in this tab ended or expired. */
  supportEnded: boolean;
}

const ANONYMOUS: AuthState = {
  status: "anonymous",
  user: null,
  firstRunComplete: null,
  featureFlags: null,
  supportSession: null,
  supportEnded: false,
};

const SUPPORT_ENDED: AuthState = { ...ANONYMOUS, supportEnded: true };

interface AuthContextValue extends AuthState {
  login: (input: PortalLoginInput) => Promise<UserPublic>;
  logout: () => Promise<void>;
  /**
   * Populate auth state from a server-issued session that didn't go
   * through `/auth/login` — currently the onboarding-accept flow, which
   * mints the same token shape via `/onboarding/invite/{token}/accept`.
   * The refresh cookie is set server-side; this just plants the access
   * token into the API client and flips status to `authed`.
   */
  setSession: (input: { user: UserPublic; access_token: string }) => void;
  /**
   * Phase C1a — flip the local first-run flag without a round-trip.
   * Called by the welcome step's complete-mutation onSuccess so the
   * AppShell guard relaxes the redirect immediately.
   */
  markFirstRunComplete: () => void;
  /** Phase SC-22 — adopt a freshly exchanged support-session token. */
  beginSupportSession: (resp: SupportSessionExchangeResponse) => Promise<void>;
  /** Phase SC-22 — end the support session server-side and lock this tab. */
  endSupportSession: () => Promise<void>;
  /** Phase SC-22 — the session expired; lock the tab without a round-trip. */
  expireSupportSession: () => void;
}

/**
 * Bearer-only mode for a support tab: no cookies leave this tab and a 401
 * ends the support view instead of attempting `/auth/refresh`, which
 * would replay (and get the BE to clear) the super-admin's own cookie.
 */
function enterSupportTransport(onEnded: () => void): void {
  api.setCookieAuth(false);
  api.setUnauthorizedHandler(onEnded);
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Cross-tenant cookie defense (stage incident 2026-05-03)
 * -------------------------------------------------------
 * The eos_refresh cookie is scoped to .eduviaos.com so it's replayed on
 * every subdomain. The BE now refuses /auth/refresh when the cookie's
 * tenant_id claim doesn't match the request's resolved tenant slug, but
 * we ALSO defend client-side: if /auth/me returns a user whose
 * tenant_id doesn't match the URL's subdomain, we treat the session as
 * invalid (force logout + drop to /login). Belt-and-braces — protects
 * against any future BE regression and against the pre-merge window
 * where one of the two layers is deployed without the other.
 *
 * Returns true when the user is good to mount, false when the URL says
 * one tenant and the JWT says another (caller must logout + drop the
 * session). super-admin is always allowed (their tenant is the synthetic
 * platform tenant, not a subdomain).
 */
function tenantContextMatchesUser(user: UserPublic): boolean {
  if (user.role === "super_admin") return true;
  const ctx = getCurrentTenant();
  // No subdomain (bare localhost / dev) — there's nothing to compare
  // against, so trust the BE. The BE has the same fall-back posture.
  if (!ctx.slug) return true;
  return ctx.slug.toLowerCase() === user.tenant_id.toLowerCase();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>({
    ...ANONYMOUS,
    status: "loading",
  });

  const expireSupportSession = React.useCallback(() => {
    clearSupportSession();
    api.setAccessToken(null);
    setState(SUPPORT_ENDED);
  }, []);

  /**
   * On mount: try to silently refresh using the HttpOnly cookie. If it
   * succeeds we mint a fresh access token and fetch /auth/me. If it
   * fails (no cookie, expired) we land in `anonymous` and the route
   * guard sends the user to /login.
   *
   * Phase SC-22 — a support tab (the `/support-session` landing route, or
   * a tab holding a stored support token) never touches the cookie.
   */
  React.useEffect(() => {
    let cancelled = false;
    const stored = readSupportSession(getCurrentTenant().slug ?? null);
    if (isSupportSessionRoute()) {
      enterSupportTransport(expireSupportSession);
      setState(ANONYMOUS);
      return;
    }
    if (stored) {
      enterSupportTransport(expireSupportSession);
      api.setAccessToken(stored.access_token);
      (async () => {
        try {
          const me = await api.get<MeResponse>("/auth/me");
          if (cancelled) return;
          if (!me.support_session || !tenantContextMatchesUser(me.user)) {
            expireSupportSession();
            return;
          }
          setState({
            status: "authed",
            user: me.user,
            firstRunComplete: me.first_run_complete ?? null,
            featureFlags: me.feature_flags ?? null,
            supportSession: me.support_session,
            supportEnded: false,
          });
        } catch {
          if (!cancelled) expireSupportSession();
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const refreshed = await api.post<{ access_token: string; expires_in: number }>(
          "/auth/refresh",
        );
        api.setAccessToken(refreshed.access_token);
        const me = await api.get<MeResponse>("/auth/me");
        if (cancelled) return;
        // Cross-tenant cookie defense — if the refreshed session belongs
        // to a different tenant than the URL we're on, drop it on the
        // floor. The BE refuses the refresh in this case, but we keep
        // the assertion as belt-and-braces so a brief deployment skew
        // can't render the wrong tenant's data even for a frame.
        if (!tenantContextMatchesUser(me.user)) {
          api.setAccessToken(null);
          // Best-effort logout — clears the cookie server-side too.
          // Failure here is non-fatal; the BE refresh will reject this
          // poisoned cookie on the next attempt regardless.
          try {
            await api.post("/auth/logout");
          } catch {
            // ignore
          }
          setState(ANONYMOUS);
          return;
        }
        setState({
          ...ANONYMOUS,
          status: "authed",
          user: me.user,
          firstRunComplete: me.first_run_complete ?? null,
          featureFlags: me.feature_flags ?? null,
        });
      } catch {
        if (!cancelled) setState(ANONYMOUS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expireSupportSession]);

  const login = React.useCallback(async (input: PortalLoginInput) => {
    // tenant_id is intentionally NOT sent in the body for the SPA
    // happy-path; the API client injects X-Tenant-Slug from the URL.
    // Only forward an explicit override (tests, dev fallback for bare
    // `localhost:3001`) when the caller passed one.
    const body: Record<string, string> = {
      email: input.email,
      password: input.password,
    };
    if (input.tenant_id) body.tenant_id = input.tenant_id;
    const resp = await api.post<LoginResponse>("/auth/login", body);
    // Cross-tenant defense (login flow): the BE checks tenant resolution
    // up front so this should never trigger on a correctly-built form.
    // Belt-and-braces against a custom client passing a body tenant_id
    // that disagrees with the URL — fail loud rather than mint a session
    // for the wrong tenant.
    if (!tenantContextMatchesUser(resp.user)) {
      throw new ApiError("This sign-in belongs to a different institution than this address.", {
        status: 401,
        code: "TENANT_MISMATCH",
      });
    }
    api.setAccessToken(resp.tokens.access_token);
    // Login itself doesn't return first_run_complete + feature_flags
    // (those live on /auth/me); fetch them eagerly so the AppShell
    // guard + Assessment write-gate have the right signal on first
    // paint instead of treating null as "complete" / "no gating".
    let firstRunComplete: boolean | null = null;
    let featureFlags: TenantFeatureFlagsView | null = null;
    try {
      const me = await api.get<MeResponse>("/auth/me");
      firstRunComplete = me.first_run_complete ?? null;
      featureFlags = me.feature_flags ?? null;
    } catch {
      // Non-fatal — worst case we render the dashboard once and the
      // next /auth/me poll corrects state. Better than blocking login.
    }
    setState({ ...ANONYMOUS, status: "authed", user: resp.user, firstRunComplete, featureFlags });
    return resp.user;
  }, []);

  const endSupportSession = React.useCallback(async () => {
    try {
      await api.post("/auth/support-session/end");
    } catch {
      // Already ended or expired server-side — the tab locks either way.
    }
    expireSupportSession();
  }, [expireSupportSession]);

  const beginSupportSession = React.useCallback(
    async (resp: SupportSessionExchangeResponse) => {
      storeSupportSession(resp);
      enterSupportTransport(expireSupportSession);
      api.setAccessToken(resp.access_token);
      const me = await api.get<MeResponse>("/auth/me");
      if (!me.support_session || !tenantContextMatchesUser(me.user)) {
        expireSupportSession();
        throw new ApiError("This support session doesn't belong to this centre.", {
          status: 401,
          code: "TENANT_MISMATCH",
        });
      }
      setState({
        status: "authed",
        user: me.user,
        firstRunComplete: me.first_run_complete ?? null,
        featureFlags: me.feature_flags ?? null,
        supportSession: me.support_session,
        supportEnded: false,
      });
    },
    [expireSupportSession],
  );

  const logout = React.useCallback(async () => {
    if (state.supportSession) {
      await endSupportSession();
      return;
    }
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network / 401 here are non-fatal — proceed with local cleanup.
      if (!(err instanceof ApiError) || err.status >= 500) {
        console.warn("logout request failed", err);
      }
    }
    api.setAccessToken(null);
    setState(ANONYMOUS);
  }, [state.supportSession, endSupportSession]);

  const setSession = React.useCallback((input: { user: UserPublic; access_token: string }) => {
    api.setAccessToken(input.access_token);
    // Onboarding-accept lands a fresh principal whose tenant was just
    // minted by the SA — first_run_complete is always False at this
    // point. Hard-code rather than re-fetch /auth/me to keep the
    // accept-page → first-run-welcome navigation snappy. featureFlags
    // stays null until the next /auth/me poll — IA write-gate treats
    // null as "no gating" so a freshly minted tenant lands unblocked.
    setState({
      ...ANONYMOUS,
      status: "authed",
      user: input.user,
      firstRunComplete: false,
    });
  }, []);

  const markFirstRunComplete = React.useCallback(() => {
    setState((prev) => (prev.status === "authed" ? { ...prev, firstRunComplete: true } : prev));
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      setSession,
      markFirstRunComplete,
      beginSupportSession,
      endSupportSession,
      expireSupportSession,
    }),
    [
      state,
      login,
      logout,
      setSession,
      markFirstRunComplete,
      beginSupportSession,
      endSupportSession,
      expireSupportSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
